import { computed, makeAutoObservable } from 'mobx';
import { list, object, primitive } from 'serializr';

import { defaultViewId, GridViewState, tempViewId } from 'controls/grid/gridViewState';
import { newGuid } from 'tools/guid';
import { copyViaSerializr, createModelSchemaWrapper } from 'framework/serializr-integration';
import { GridDataEntry } from 'controls/grid/gridDataEntry';
import { GridStore } from './gridStore';
import { MobxManager } from 'framework/mobx-integration';
import { translator } from 'core/localization';

const i18n = translator({
	'Default view': { no: 'Standard visning' },
	'New view': { no: 'Ny visning' },
	'External view': { no: 'Ekstern visning' },
});

export class GridState<DataEntry extends GridDataEntry> {
	initialViewId: string;
	_currentViewId: string;

	views: GridViewState<DataEntry>[] = [];

	//an initial copy of views. Is used to react on 'reset' button
	initialViews: GridViewState<DataEntry>[] = [];

	mobx = new MobxManager();

	deletedViews: string[] = [];

	get currentViewId() {
		return this._currentViewId ?? this.initialViewId ?? defaultViewId;
	}

	set currentViewId(id: string) {
		this._currentViewId = id;
	}

	get columns() {
		return this.currentView.columns;
	}

	get sortingOrder() {
		return this.currentView.sortingOrder;
	}

	get showInlineFilters() {
		return this.currentView.showInlineFilters;
	}

	set showInlineFilters(value: boolean) {
		this.currentView.showInlineFilters = value;
	}

	set sortingOrder(value) {
		this.currentView.sortingOrder = value;
	}

	get filters() {
		return this.currentView.filters;
	}

	set filters(value) {
		this.currentView.filters = value;
	}

	get searchString() {
		return this.currentView.searchString;
	}

	set searchString(value) {
		this.currentView.searchString = value;
	}

	get customPayload() {
		return this.currentView?.customPayload;
	}

	get groupBy(): (keyof DataEntry)[] {
		return this.currentView.groupBy;
	}

	set groupBy(value: (keyof DataEntry)[]) {
		this.currentView.groupBy = value;
	}

	get groupByMode() {
		return this.currentView.groupByMode;
	}

	set groupByMode(value: GridViewState<DataEntry>['groupByMode']) {
		this.currentView.groupByMode = value;
	}

	get collapsedGroups() {
		return this.currentView.collapsedGroups;
	}

	set customPayload(value) {
		this.currentView.customPayload = value;
	}

	get customData() {
		return this.currentView.customData;
	}

	set customData(value) {
		this.currentView.customData = value;
	}

	get currentView() {
		return this.views.find((x) => x.id == this.currentViewId) ?? this.defaultView;
	}

	get defaultView() {
		return this.views.find((x) => x.id == defaultViewId);
	}

	constructor() {
		makeAutoObservable(this, {
			currentView: computed({ keepAlive: true }),
		});

		this.mobx.reaction(
			() => this.currentViewId,
			() => {
				if (this.currentViewId != tempViewId) {
					const tempViewIndex = this.views.findIndex((x) => x.id == tempViewId);
					if (tempViewIndex != -1) {
						this.views.splice(tempViewIndex, 1);
					}
				}
			},
		);
	}

	ensureDefaultViewExists() {
		let defaultView = this.views.find((x) => x.id == defaultViewId);

		if (defaultView == null) {
			defaultView = new GridViewState<DataEntry>();
			defaultView.id = defaultViewId;
			defaultView.name = i18n('Default view');

			this.views.push(defaultView);
		} else {
			defaultView.name = i18n('Default view');
		}
	}

	duplicateCurrentView = () => {
		const newView = copyViaSerializr<GridViewState<DataEntry>>(this.currentView);
		newView.name = i18n('New view');
		newView.id = newGuid();
		this.views.push(newView);

		this.currentViewId = newView.id;

		return newView.id;
	};

	deleteView = () => {
		if (!this.canDeleteView()) return;

		this.deletedViews.push(this.currentViewId);
		const actualViewIndex = this.views.findIndex((x) => x.id == this.currentViewId);

		if (this.initialViewId == this.currentViewId) {
			this.initialViewId = defaultViewId;
		}

		this.currentViewId = this.views[Math.max(0, actualViewIndex - 1)].id;

		this.views.splice(actualViewIndex, 1);
	};

	canDeleteView = () => {
		return this.currentViewId && this.currentViewId != defaultViewId;
	};

	validate = (store: GridStore<DataEntry>) => {
		this.ensureDefaultViewExists();
		this.views.forEach((x) => x.validate(store));
	};

	createViewsSnapshot() {
		this.initialViews = this.views.map((x) => copyViaSerializr<GridViewState<DataEntry>>(x));
		this.views.forEach((x) => x.calculateHash());
	}

	reset = (store: GridStore<DataEntry>) => {
		if (this.currentView.isTemporary()) {
			this.currentViewId = defaultViewId;
			return;
		}

		const originalView = this.initialViews.find((x) => x.id == this.currentViewId);
		const notSaved = !originalView;

		if (this.currentView.isDefault() || notSaved) {
			this.currentView.resetToDefault(store);
			return;
		}

		const originalViewCopy = copyViaSerializr<GridViewState<DataEntry>>(originalView);
		originalViewCopy.validate(store);
		originalViewCopy.calculateHash();

		const index = this.views.findIndex((x) => x.id == this.currentViewId);
		this.views.splice(index, 1, originalView);
	};

	switchToTempView = () => {
		if (this.currentViewId == tempViewId) return;

		const newView = copyViaSerializr<GridViewState<DataEntry>>(this.currentView);
		newView.name = i18n('External view');
		newView.id = tempViewId;

		this.views.push(newView);
		this.currentViewId = tempViewId;
	};

	switchToDefaultView = () => {
		this.currentViewId = defaultViewId;
	};

	clearSorting = () => {
		this.sortingOrder = [];
		this.columns.forEach((x) => (x.sorting = null));
	};

	isGroupCollapsed(id: string) {
		return this.collapsedGroups.indexOf(id) != -1;
	}

	destroy() {
		this.mobx.destroy();
	}
}

createModelSchemaWrapper(GridState, {
	initialViewId: primitive(),
	views: list(object(GridViewState), {
		beforeDeserialize: (callback, jsonValue, jsonParentValue: object) => {
			//the field got renamed from viewsStored to views, backwards compatibility maintained here
			if (jsonValue == null && 'viewsStored' in jsonParentValue && jsonParentValue.viewsStored != null) {
				jsonValue = jsonParentValue.viewsStored;
			}

			callback(null, jsonValue);
		},
	}),
});
